import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import './styles.css';
// 1.2.0: each stream's styles in a file of its own (the merge step may fold them into styles.css).
import './styles/panes.css';
import './styles/selection.css';
import './styles/batch.css';
import './styles/masters.css';
import './styles/flows.css';
// 1.2.2–1.2.3: versions (Upload…, the Versions tab, Old versions).
import './styles/versions.css';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ApiError } from './api/client';
import { onSessionError } from './api/session-end';

/**
 * The server ended this session (idle too long, a closed laptop, a sleeping tab; since 1.2.4 also any other sign-out):
 * back to sign-in, with why.
 */
function onApiError(err: unknown) {
  onSessionError(queryClient, err);
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 3,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* 1.3.0 fix: an error anywhere shows itself instead of leaving a blank page (pages may have their own). */}
        <ErrorBoundary testId="app-error" title="PRISM could not show this page">
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
